// src/components/admin/PagePermissionWrapper.tsx
//
// ─── UNIVERSAL WRITE-PROTECTION WRAPPER ─────────────────────────────────────
//
// This component automatically disables edit/delete/add buttons
// for users who only have VIEW access — zero changes needed in individual pages.
//
// HOW TO USE — wrap every admin page in App.tsx:
//
//   import PagePermissionWrapper from './components/admin/PagePermissionWrapper';
//
//   <Route path="/productManagement" element={
//     <AdminLayout>
//       <PagePermissionWrapper menuId="product_management">
//         <ProductManagement />
//       </PagePermissionWrapper>
//     </AdminLayout>
//   } />
//
// That's it. If user has only "view" permission:
//   - Edit / Delete IconButtons   → hidden
//   - Add / Create / Save Buttons → hidden
//   - Read-only banner            → shown at top
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef } from 'react';
import { Box, Alert } from '@mui/material';
import { usePermission } from '../../features/admin/PermissionContext.tsx';

interface Props {
  menuId: string;
  children: React.ReactNode;
}

const PagePermissionWrapper: React.FC<Props> = ({ menuId, children }) => {
  const { canWrite } = usePermission(menuId);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (canWrite) return; // Write access → do nothing

    const el = wrapperRef.current;
    if (!el) return;

    // ── Strategy: inject a <style> tag that hides write-only elements ────────
    // We target MUI components by their common patterns:
    //   - IconButtons containing Edit / Delete / Add icons
    //   - Buttons with write-action text
    //   - FAB buttons
    // This works without touching any page code.

    const styleId = `perm-write-block-${menuId}`;
    if (document.getElementById(styleId)) return; // already injected

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Hide edit/delete/add icon buttons */
      [data-perm-wrapper="${menuId}"] [data-write-action],
      [data-perm-wrapper="${menuId}"] .write-action {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const s = document.getElementById(styleId);
      if (s) s.remove();
    };
  }, [canWrite, menuId]);

  // For view-only: we use React cloning approach to intercept children
  // and inject canWrite prop down, PLUS we add CSS class markers

  return (
    <Box ref={wrapperRef} data-perm-wrapper={menuId} sx={{ width: '100%', height: '100%' }}>
      {/* View-only banner */}
      {!canWrite && (
        <Alert
          severity="info"
          sx={{
            mx: 3,
            mt: 2,
            mb: 0,
            borderRadius: '8px',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          👁️ You have <strong>view-only</strong> access to this page. Edit, Delete and Add actions are disabled.
        </Alert>
      )}

      {/* Overlay approach: when canWrite=false, block all pointer events on write elements */}
      {!canWrite ? (
        <WriteBlockedContent>{children}</WriteBlockedContent>
      ) : (
        children
      )}
    </Box>
  );
};

// ─── WriteBlockedContent ─────────────────────────────────────────────────────
// Wraps children in a div and uses MutationObserver to find and hide
// edit/delete/add buttons as they render (handles dynamic tables too)

const WRITE_BUTTON_PATTERNS = [
  // Icon aria-labels
  '[aria-label*="edit" i]',
  '[aria-label*="delete" i]',
  '[aria-label*="remove" i]',
  '[aria-label*="update" i]',
  // MUI IconButton titles
  '[title*="Edit" i]',
  '[title*="Delete" i]',
  '[title*="Remove" i]',
  // Buttons by text content — handled in JS below
];

// Button text patterns that indicate write actions
const WRITE_TEXT_PATTERNS = [
  'add', 'create', 'new', 'edit', 'update', 'delete',
  'remove', 'save', 'submit', 'upload', 'import',
  'publish', 'approve', 'reject', 'cancel order',
];

function hideWriteElements(container: HTMLElement) {
  // 1. Hide by aria-label / title patterns
  const selectorMatches = container.querySelectorAll(WRITE_BUTTON_PATTERNS.join(','));
  selectorMatches.forEach((el) => {
    (el as HTMLElement).style.display = 'none';
  });

  // 2. Hide MUI IconButtons that contain SVG edit/delete icons
  // EditIcon has data-testid="EditIcon", DeleteIcon has "DeleteIcon" etc.
  const svgIcons = container.querySelectorAll(
    '[data-testid="EditIcon"], [data-testid="DeleteIcon"], [data-testid="AddIcon"],' +
    '[data-testid="AddCircleIcon"], [data-testid="CreateIcon"], [data-testid="RemoveCircleIcon"],' +
    '[data-testid="ModeEditIcon"], [data-testid="DeleteForeverIcon"], [data-testid="EditNoteIcon"]' +
    '[data-testid="AddCircleOutlineIcon"],[data-testid="DriveFileRenameOutlineIcon"]' 
  );
  svgIcons.forEach((icon) => {
    // Walk up to find the nearest button/IconButton and hide it
    let el: HTMLElement | null = icon as HTMLElement;
    for (let i = 0; i < 4; i++) {
      el = el?.parentElement ?? null;
      if (el && (el.tagName === 'BUTTON' || el.getAttribute('role') === 'button')) {
        el.style.display = 'none';
        break;
      }
    }
  });

  // 3. Hide buttons/chips by text content
  const buttons = container.querySelectorAll('button, [role="button"]');
  buttons.forEach((btn) => {
    const text = (btn.textContent || '').toLowerCase().trim();
    const isWrite = WRITE_TEXT_PATTERNS.some((p) => text.startsWith(p) || text === p);
    if (isWrite) {
      (btn as HTMLElement).style.display = 'none';
    }
  });
}

const WriteBlockedContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Initial hide
    hideWriteElements(container);

    // Watch for dynamically added elements (tables with rows loaded async)
    const observer = new MutationObserver(() => {
      hideWriteElements(container);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      {children}
    </div>
  );
};

export default PagePermissionWrapper;